import React from 'react';
import PropTypes from 'prop-types';

import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import Icon from '@mui/material/Icon';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import InputAdornment from '@mui/material/InputAdornment';
import Tooltip from '@mui/material/Tooltip';

import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';

function toRuleKey(value) {
  return String(value || '')
    .trim()
    .toLocaleLowerCase('tr-TR');
}

function lessonLabel(count) {
  const n = Number(count || 0);
  if (n <= 1) return '1 kişilik seans ücreti';
  return `${n} kişilik seans ücreti`;
}

const fieldSx = {
  '& .MuiInputBase-root': {
    height: 40,
    fontSize: 13,
    borderRadius: '12px',
    backgroundColor: '#fff',
  },
  '& .MuiInputLabel-root': {
    fontSize: 12,
  },
};

function RuleRow({ label, helperText, value, onChange, onRemove, removable }) {
  return (
    <MDBox
      sx={{
        p: 1,
        borderRadius: '14px',
        border: '1px solid rgba(0,0,0,0.08)',
        bgcolor: 'rgba(255,255,255,0.96)',
      }}
    >
      <MDBox
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 1,
          mb: 0.8,
        }}
      >
        <MDBox sx={{ minWidth: 0 }}>
          <MDTypography variant="button" fontWeight="bold">
            {label}
          </MDTypography>
          {helperText ? (
            <MDTypography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
              {helperText}
            </MDTypography>
          ) : null}
        </MDBox>

        {removable ? (
          <Tooltip title="Listeden kaldır">
            <IconButton size="small" onClick={onRemove}>
              <Icon fontSize="small">delete_outline</Icon>
            </IconButton>
          </Tooltip>
        ) : null}
      </MDBox>

      <TextField
        fullWidth
        size="small"
        label="Ücret"
        type="number"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        sx={fieldSx}
        InputProps={{
          endAdornment: <InputAdornment position="end">₺</InputAdornment>,
          inputProps: { min: 0 },
        }}
      />
    </MDBox>
  );
}

RuleRow.propTypes = {
  label: PropTypes.string.isRequired,
  helperText: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func,
  removable: PropTypes.bool,
};

RuleRow.defaultProps = {
  helperText: '',
  value: '',
  onRemove: undefined,
  removable: false,
};

export default function PayrollPricingRulesModal({
  open,
  onClose,
  lessonRules,
  packageRules,
  packageOptions,
  onLessonRateChange,
  onPackageRateChange,
  onAddPackageRule,
  onRemovePackageRule,
}) {
  const [selectedPackageKey, setSelectedPackageKey] = React.useState('');

  React.useEffect(() => {
    if (!open) setSelectedPackageKey('');
  }, [open]);

  const selectedPackageOption =
    packageOptions.find((item) => toRuleKey(item.key) === toRuleKey(selectedPackageKey)) || null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ px: 2, py: 1.6 }}>
        <MDBox display="flex" alignItems="center" justifyContent="space-between" gap={1}>
          <MDBox>
            <MDTypography variant="h6" fontWeight="bold">
              Ücret Kuralları
            </MDTypography>
            <MDTypography variant="caption" sx={{ color: 'text.secondary' }}>
              Seans ücreti kişi sayısına göre hesaplanır. Paket için özel fiyat varsa seans ücretini
              ezer.
            </MDTypography>
          </MDBox>

          <MDButton variant="outlined" color="dark" onClick={onClose} sx={{ borderRadius: '12px' }}>
            Kapat
          </MDButton>
        </MDBox>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ p: 2 }}>
        <MDBox sx={{ display: 'grid', gap: 2 }}>
          <Alert severity="info" sx={{ borderRadius: '14px' }}>
            Grup seanslarında ücret tek kez hesaplanır. Aynı seanstaki diğer kişilerde bu ücret
            sadece bilgi olarak görünür.
          </Alert>

          <MDBox sx={{ display: 'grid', gap: 1 }}>
            <MDBox display="flex" alignItems="center" gap={0.8} flexWrap="wrap">
              <MDTypography variant="button" fontWeight="bold">
                Seans ücretleri
              </MDTypography>
              <Chip
                size="small"
                label={`${lessonRules.length} kalem`}
                sx={{ borderRadius: '999px', fontWeight: 800 }}
              />
            </MDBox>

            <Grid container spacing={1}>
              {lessonRules.map((rule) => (
                <Grid key={rule.key} item xs={12} md={6}>
                  <RuleRow
                    label={lessonLabel(rule.participantCount)}
                    helperText={`Bu dönemde ${rule.participantCount} kişilik seans bulundu.`}
                    value={rule.rate}
                    onChange={(nextValue) => onLessonRateChange(rule.key, nextValue)}
                  />
                </Grid>
              ))}
            </Grid>
          </MDBox>

          <MDBox sx={{ display: 'grid', gap: 1 }}>
            <MDBox
              sx={{
                display: 'flex',
                alignItems: { xs: 'stretch', md: 'center' },
                justifyContent: 'space-between',
                gap: 1,
                flexDirection: { xs: 'column', md: 'row' },
              }}
            >
              <MDBox>
                <MDTypography variant="button" fontWeight="bold">
                  Paket override ücretleri
                </MDTypography>
                <MDTypography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                  Aynı pakete ait seanslar için kişi sayısı ücretini ezmek üzere özel fiyat
                  tanımlayabilirsin.
                </MDTypography>
              </MDBox>

              <MDBox
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  flexWrap: 'wrap',
                  justifyContent: { xs: 'stretch', md: 'flex-end' },
                }}
              >
                <TextField
                  select
                  size="small"
                  label="Paket ekle"
                  value={selectedPackageKey}
                  onChange={(e) => setSelectedPackageKey(e.target.value)}
                  sx={{ minWidth: { xs: '100%', md: 280 }, ...fieldSx }}
                >
                  {packageOptions.length ? (
                    packageOptions.map((item) => (
                      <MenuItem key={item.key} value={item.key}>
                        {item.label}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem value="" disabled>
                      Paket bulunamadı
                    </MenuItem>
                  )}
                </TextField>

                <Button
                  variant="contained"
                  startIcon={<Icon>add</Icon>}
                  disabled={!selectedPackageOption}
                  onClick={() => {
                    if (!selectedPackageOption) return;
                    onAddPackageRule(selectedPackageOption);
                    setSelectedPackageKey('');
                  }}
                  sx={{
                    height: 40,
                    borderRadius: '999px',
                    textTransform: 'none',
                    fontWeight: 800,
                    color: '#fff !important',
                    boxShadow: 'none',
                  }}
                >
                  Listeye ekle
                </Button>
              </MDBox>
            </MDBox>

            {packageRules.length ? (
              <Grid container spacing={1}>
                {packageRules.map((rule) => (
                  <Grid key={rule.key} item xs={12} md={6}>
                    <RuleRow
                      label={rule.label}
                      helperText={rule.sourceLabel}
                      value={rule.rate}
                      onChange={(nextValue) => onPackageRateChange(rule.key, nextValue)}
                      onRemove={() => onRemovePackageRule(rule.key)}
                      removable
                    />
                  </Grid>
                ))}
              </Grid>
            ) : (
              <MDBox
                sx={{
                  p: 1.2,
                  borderRadius: '14px',
                  border: '1px dashed rgba(0,0,0,0.16)',
                  bgcolor: 'rgba(0,0,0,0.02)',
                }}
              >
                <MDTypography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>
                  Paket override fiyatı girilmedi. Seans ücretleri kullanılacak.
                </MDTypography>
              </MDBox>
            )}
          </MDBox>
        </MDBox>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 2, py: 1.25 }}>
        <MDButton variant="gradient" color="info" onClick={onClose}>
          Tamam
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

PayrollPricingRulesModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  lessonRules: PropTypes.arrayOf(PropTypes.object),
  packageRules: PropTypes.arrayOf(PropTypes.object),
  packageOptions: PropTypes.arrayOf(PropTypes.object),
  onLessonRateChange: PropTypes.func.isRequired,
  onPackageRateChange: PropTypes.func.isRequired,
  onAddPackageRule: PropTypes.func.isRequired,
  onRemovePackageRule: PropTypes.func.isRequired,
};

PayrollPricingRulesModal.defaultProps = {
  lessonRules: [],
  packageRules: [],
  packageOptions: [],
};
